import React, { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { 
  trackPaymentProcessing,
  trackAccountCreation 
} from '@/utils/checkout-analytics';
import { 
  recordFunnelStep,
  trackPaymentSuccess,
  FUNNEL_STEPS 
} from '@/utils/conversion-funnel';
import AccountSetupFlow from './AccountSetupFlow';

const PaymentSuccess = ({ 
  sessionId, 
  customerEmail,
  customerPhone,
  stripeCustomerId,
  onAccountSetupComplete 
}) => {
  const [showAccountSetup, setShowAccountSetup] = useState(false);


  // Track payment success
  useEffect(() => {
    // Track payment success
    trackPaymentProcessing('success', {
      session_id: sessionId,
      customer_email: customerEmail,
    });
    
    // Track payment success metrics
    trackPaymentSuccess({
      session_id: sessionId,
      customer_email: customerEmail,
    });
    
    // Record funnel step
    recordFunnelStep(FUNNEL_STEPS.PAYMENT_SUCCESS, {
      session_id: sessionId,
      customer_email: customerEmail,
    });
    
    // Show payment confirmation first, then account setup
    setTimeout(() => {
      setShowAccountSetup(true);
      
      // Track account creation start
      trackAccountCreation('started', {
        session_id: sessionId,
        customer_email: customerEmail,
        stripeCustomerId: stripeCustomerId,
      });
      
      recordFunnelStep(FUNNEL_STEPS.ACCOUNT_CREATION_STARTED, {
        session_id: sessionId,
        customer_email: customerEmail,
      });
    }, 2000);
  }, [sessionId, customerEmail, stripeCustomerId]);

  const handleAccountSetupComplete = (authToken) => {
    // Track account setup completion
    trackAccountCreation('success', {
      session_id: sessionId,
      customer_email: customerEmail,
      has_auth_token: !!authToken,
    });
    
    recordFunnelStep(FUNNEL_STEPS.ACCOUNT_CREATION_SUCCESS, {
      session_id: sessionId,
      customer_email: customerEmail,
      has_auth_token: !!authToken,
    });
    
    if (onAccountSetupComplete) {
      onAccountSetupComplete(authToken);
    }
  };

  // Show account setup flow if ready
  if (showAccountSetup && stripeCustomerId && customerEmail && customerPhone) {
    return (
      <AccountSetupFlow
        stripeCustomerId={stripeCustomerId}
        email={customerEmail}
        phoneNumber={customerPhone}
        onComplete={handleAccountSetupComplete}
      />
    );
  }

  // Show payment confirmation
  return (
    <div className="text-center space-y-6 py-8">
      <div className="flex justify-center">
        <CheckCircle className="h-16 w-16 text-green-500" />
      </div>
      
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-green-700">Payment Successful!</h2>
        <p className="text-muted-foreground">
          Thank you for subscribing to Lexi Tips. We're setting up your account now...
        </p>
      </div>
      
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
        <h3 className="font-medium text-green-800">What's Happening Next</h3>
        <ul className="text-sm text-green-700 space-y-1 text-left">
          <li>• Creating your account with Twilio authentication</li>
          <li>• Sending welcome SMS to {customerPhone}</li>
          <li>• Sending welcome email to {customerEmail}</li>
          <li>• Setting up your access to premium tips</li>
        </ul>
      </div>
      
      {!showAccountSetup && (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F0A7D6]"></div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;